import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { saasApi } from '../../lib/api';

function fmtMoney(cents, currency = 'BRL') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('pt-BR') : '—';
}

const invoiceStatusLabel = { PENDING: 'Pendente', PAID: 'Paga', FAILED: 'Falhou', CANCELED: 'Cancelada' };
const subStatusLabel = { TRIALING: 'Trial', ACTIVE: 'Ativa', PAST_DUE: 'Vencida', CANCELED: 'Cancelada' };
const methodLabel = { STRIPE: 'Stripe', PIX: 'Pix', MANUAL: 'Manual' };

export default function AdminTenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function load() {
    setError('');
    return Promise.all([saasApi.getTenant(id), saasApi.listPlans()])
      .then(([t, p]) => {
        setTenant(t);
        setPlans(p.filter((x) => x.active));
      })
      .catch((e) => setError(e.message));
  }

  useEffect(() => {
    load();
  }, [id]);

  async function checkout(planId) {
    setBusy(true);
    setError('');
    try {
      const res = await saasApi.checkout(id, { planId });
      if (res.url) {
        window.location.href = res.url;
      } else {
        await load();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleStatus() {
    setBusy(true);
    setError('');
    try {
      await saasApi.updateTenant(id, { status: tenant.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function payInvoice(invoiceId) {
    setBusy(true);
    setError('');
    try {
      await saasApi.payInvoice(invoiceId);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p className="text-[11px] text-error">{error}</p>;
  if (!tenant) return <p className="text-[11px] text-on-surface-variant">Carregando…</p>;

  const currentPlanId = tenant.plan?.id ?? '';

  return (
    <div className="flex flex-col gap-base">
      <button onClick={() => navigate(-1)} className="text-[11px] text-on-surface-variant hover:text-primary w-fit">
        ← Voltar
      </button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="studio-uniform-title font-h2 text-h2 text-on-surface">{tenant.name}</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-1">
            {tenant.slug} · {tenant._count.users} usuários · {tenant._count.templates} templates
          </p>
        </div>
        <button
          onClick={toggleStatus}
          disabled={busy}
          className={`rounded-xl px-4 py-2 font-medium transition-colors disabled:opacity-50 ${tenant.status === 'ACTIVE'
              ? 'bg-error-container text-on-error-container hover:bg-error-container/70'
              : 'bg-tertiary-container text-on-tertiary-container hover:bg-tertiary-container/70'
            }`}
        >
          {tenant.status === 'ACTIVE' ? 'Suspender tenant' : 'Reativar tenant'}
        </button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="bg-surface rounded-2xl shadow-sm p-6">
          <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">Assinatura</h3>
          <div className="mt-3 space-y-1 font-body-md text-body-md">
            <Row label="Plano" value={tenant.plan?.name ?? 'Sem plano'} />
            <Row label="Status" value={subStatusLabel[tenant.subscription?.status] ?? 'Sem assinatura'} />
            <Row label="Processadora" value={methodLabel[tenant.subscription?.provider] ?? '—'} />
            <Row label="Próxima renovação" value={fmtDate(tenant.subscription?.currentPeriodEnd)} />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const fd = new FormData(e.currentTarget);
              checkout(fd.get('planId'));
            }}
            className="mt-4 space-y-3"
          >
            <select
              name="planId"
              defaultValue={currentPlanId}
              className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px]"
            >
              <option value="">Selecionar plano…</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {fmtMoney(p.priceCents, p.currency)}
                </option>
              ))}
            </select>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-primary px-4 py-2 font-medium text-on-primary hover:bg-primary-fixed-dim transition-colors disabled:opacity-50"
            >
              {busy ? 'Processando…' : 'Assinar plano'}
            </button>
          </form>
          <p className="mt-2 text-[10px] text-on-surface-variant">
            Com Stripe configurado você é direcionado ao checkout. Sem Stripe, o plano é ativado e uma fatura é gerada.
          </p>
        </div>

        <div className="bg-surface rounded-2xl shadow-sm p-6">
          <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">Faturas</h3>
          {tenant.invoices.length === 0 ? (
            <p className="mt-3 text-[11px] text-on-surface-variant">Nenhuma fatura gerada.</p>
          ) : (
            <ul className="mt-3 space-y-2">
              {tenant.invoices.map((inv) => (
                <li key={inv.id} className="flex items-center justify-between rounded-xl border border-surface-variant px-4 py-3">
                  <div>
                    <div className="font-medium text-on-surface">
                      {fmtMoney(inv.amountCents, inv.currency)}
                      <span className="text-[10px] text-on-surface-variant ml-2">{methodLabel[inv.paymentMethod]}</span>
                    </div>
                    <div className="text-[10px] text-on-surface-variant">Criada em {fmtDate(inv.createdAt)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={inv.status}>{invoiceStatusLabel[inv.status]}</Badge>
                    {inv.status === 'PENDING' && (
                      <button
                        onClick={() => payInvoice(inv.id)}
                        disabled={busy}
                        className="rounded-lg bg-tertiary-container px-3 py-1 text-[10px] font-medium text-on-tertiary-container disabled:opacity-50"
                      >
                        Confirmar pago (Pix)
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="bg-surface rounded-2xl shadow-sm p-6">
        <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">Usuários</h3>
        <ul className="mt-3 space-y-1 font-body-md text-body-md">
          {tenant.users.map((u) => (
            <li key={u.id} className="flex justify-between border-b border-surface-container-highest py-2">
              <span className="text-on-surface">{u.name}</span>
              <span className="text-[11px] text-on-surface-variant">
                {u.email} · {u.role.toLowerCase()}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <li className="flex justify-between border-b border-surface-container-highest py-2">
      <span className="text-on-surface-variant">{label}</span>
      <span className="font-medium text-on-surface">{value}</span>
    </li>
  );
}

function Badge({ tone, children }) {
  const styles = {
    PAID: 'bg-tertiary-container text-on-tertiary-container',
    PENDING: 'bg-surface-container-highest text-on-surface-variant',
    FAILED: 'bg-error-container text-on-error-container',
    CANCELED: 'bg-surface-container-highest text-on-surface-variant',
  };
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-label-sm ${styles[tone] ?? ''}`}>{children}</span>;
}


